import { useCallback, useMemo } from 'react';
import { getMapModeStrategy } from '../mapModes';
import type { DigitalTwinContext, Infrastructure, MapModeContext } from '../mapModes/MapModeStrategy';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { useDigitalTwinsContext } from '@/contexts/DigitalTwinsContext';
import type { useMapPopup } from './useMapPopup';

interface SecurityData {
	role?: string;
}

interface UseMapModeStrategyParams {
	contextValue: DashboardContextValueInterface;
	digitalTwinsContext: ReturnType<typeof useDigitalTwinsContext>;
	isDmaOverlayEnabled: boolean;
	popup: ReturnType<typeof useMapPopup>;
}

export function useMapModeStrategy({
	contextValue,
	digitalTwinsContext,
	isDmaOverlayEnabled,
	popup,
}: UseMapModeStrategyParams) {
	const {
		hoveredParameterClass,
		lockedParameterClass,
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
		hiddenParameterClasses,
	} = digitalTwinsContext;

	const mapContext = useMemo<MapModeContext>(() => ({
		selectedLocation: contextValue.selectedLocation,
		selectedSensors: contextValue.selectedSensors,
		activatedDigitalTwinMode: contextValue.activatedDigitalTwinMode,
		activatedInflowMode: contextValue.activatedInflowMode,
		activatedDmaMode: isDmaOverlayEnabled,
		activatedSewerAnalysisMode: contextValue.activatedSewerAnalysisMode,
		selectedDate: contextValue.selectedDate,
		activatedMmsMode: contextValue.activatedMmsMode,
		selectedMmsId: contextValue.selectedMmsId,
		selectedMmsFileName: contextValue.selectedMmsFileName,
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
	}), [
		contextValue.selectedLocation,
		contextValue.selectedSensors,
		contextValue.activatedDigitalTwinMode,
		contextValue.activatedInflowMode,
		contextValue.selectedDate,
		isDmaOverlayEnabled,
		contextValue.activatedSewerAnalysisMode,
		contextValue.activatedMmsMode,
		contextValue.selectedMmsId,
		contextValue.selectedMmsFileName,
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
	]);

	const digitalTwinContextForStrategy = useMemo<DigitalTwinContext>(() => ({
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
		hoveredParameterClass,
		lockedParameterClass,
		hiddenParameterClasses,
	}), [
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
		hoveredParameterClass,
		lockedParameterClass,
		hiddenParameterClasses,
	]);

	const strategy = useMemo(() => {
		return getMapModeStrategy(mapContext);
	}, [
		mapContext.activatedMmsMode,
		mapContext.activatedDigitalTwinMode,
		mapContext.activatedInflowMode,
		mapContext.activatedDmaMode,
		mapContext.activatedSewerAnalysisMode,
	]);

	const currentSecurity: SecurityData = JSON.parse(localStorage.getItem('security') ?? '{}');
	const isAdmin = currentSecurity.role === 'Administrator';

	const handleAssetClick = useCallback(async (infra: Infrastructure) => {
		popup.setPopupCoordinates(infra.position as [number, number]);

		try {
			const popupData = await strategy.handleAssetClick(infra, mapContext);

			popup.setInfraInfo(popupData);

			popup.setPopupAdditionalData({
				selectedDigitalTwin: selectedDigitalTwinsData?.find(
					(dt) => dt.inpId?.toString() === selectedDigitalTwinId
				),
				isAdmin,
			});

			if (popupData) {
				popup.setShowPopup(true);
			}
		} catch (error) {
			console.error('Error handling asset click:', error);
		}
	}, [strategy, mapContext, selectedDigitalTwinsData, selectedDigitalTwinId, isAdmin]);

	return { strategy, mapContext, digitalTwinContextForStrategy, handleAssetClick };
}
